import PropTypes from 'prop-types'
import { useState } from 'react'
import { Table, Typography, Col, Tooltip, Switch } from 'antd'
import { Link, generatePath } from 'react-router-dom'
import styled from 'styled-components'
import { SUBSCRIPTION_TIER } from '@qflow/constants'
import { colours } from '@qflow/theme'
import * as Icons from '@ant-design/icons'

import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'
import moment from 'moment'

export function AccountsList({ userAccountMappings }) {
  const [includeClosedOut, setIncludeClosedOut] = useState(false)

  const formatTableData = () => {
    let formattedData = []

    if (userAccountMappings && userAccountMappings.length > 0) {
      formattedData = userAccountMappings
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
        )
        .map(dataRow => {
          const originalProjectCount = dataRow.projects.length
          const projects = dataRow.projects
            .map(projectRow => {
              return {
                key: projectRow.projectId,
                projectId: projectRow.projectId,
                projectName: projectRow.name,
                country: projectRow.country,
                isAlertsEnabled: projectRow.isAlertsEnabled,
                isClosedOut: projectRow.isClosedOut,
                subscriptionTier: projectRow.subscriptionTier,
                isExtendedProductGroupsEnabled:
                  projectRow.isExtendedProductGroupsEnabled,
                isUniclassCodesEnabled: projectRow.isUniclassCodesEnabled,
                isDesignMatchesEnabled: projectRow.isDesignMatchesEnabled,
                isAutodeskEnabled: projectRow.isAutodeskEnabled,
                isOneClickLcaEnabled: projectRow.isOneClickLcaEnabled
              }
            })
            .filter(project => includeClosedOut || !project.isClosedOut)

          return {
            key: dataRow.accountId,
            accountId: dataRow.accountId,
            name: dataRow.name,
            companyName: dataRow.companyName,
            renewalDate: dataRow.renewalDate,
            createdAt: dataRow.createdAt,
            isEnabled: dataRow.isEnabled,
            isAdmin: dataRow.isAdmin,
            projects: projects,
            originalProjectCount: originalProjectCount
          }
        })
        .filter(
          account =>
            account.originalProjectCount === 0 || account.projects.length > 0
        )
    }

    return formattedData
  }

  const columns = [
    {
      title: 'Account Name',
      width: '30%',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (_, account) => (
        <>
          {account.isAdmin && (
            <Link
              to={generatePath(ROUTES.ACCOUNTS.EDIT, {
                accountId: account.accountId
              })}
            >
              Edit Account
            </Link>
          )}
          <Col>
            <Typography.Text strong={true}>{account.name}</Typography.Text>
          </Col>
          <Col>
            <Typography.Text italic={true} copyable>
              {account.accountId}
            </Typography.Text>
          </Col>
          <Col>
            <Typography.Text>
              {account.companyName
                ? `Company: ${account.companyName}`
                : 'No Company Name Set'}
            </Typography.Text>
          </Col>
          <Col>
            <Typography.Text>
              {account.renewalDate
                ? `Renewal Date: ${moment(account.renewalDate).format('DD/MM/YYYY')}`
                : 'No Renewal Date Set'}
            </Typography.Text>
          </Col>
        </>
      )
    },
    {
      title: 'Projects',
      key: 'accountId',
      render: (_, account) => {
        const sortedProjects = account.projects.sort((a, b) =>
          a.projectName.localeCompare(b.projectName)
        )
        const topFiveProjects = sortedProjects.slice(0, 5)
        const remainingProjects = sortedProjects.slice(5)

        return (
          <Col>
            <Link
              to={generatePath(ROUTES.PROJECTS.LIST, {
                accountId: account.accountId
              })}
            >
              Manage Projects
            </Link>
            {topFiveProjects.map(project => (
              <div key={project.key}>
                <Flag
                  country={project.country}
                  style={{
                    width: '21px',
                    margin: '0 5px 0 -26px',
                    verticalAlign: 'middle'
                  }}
                />
                <div
                  style={{
                    display: 'inline-flex',
                    gap: '0.5rem',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <div style={{ minWidth: 'calc(100% - 12rem)' }}>
                    <Typography.Text strong={true}>
                      {project.projectName}
                    </Typography.Text>{' '}
                    <Typography.Text italic={true}>
                      {project.projectId}
                    </Typography.Text>
                    <FeatureIcons project={project} />
                  </div>
                  <div style={{ minWidth: '5rem', textAlign: 'center' }}>
                    {project.subscriptionTier && (
                      <SubscriptionTier tier={project.subscriptionTier} />
                    )}
                  </div>
                  <div style={{ minWidth: '6rem' }}>
                    {project.isClosedOut && <ClosedOut />}
                  </div>
                </div>
              </div>
            ))}
            {remainingProjects.length > 0 && (
              <details>
                <summary>
                  <Typography.Text type="secondary">See More</Typography.Text>
                </summary>

                {remainingProjects.map(project => (
                  <div key={project.key}>
                    <Flag
                      country={project.country}
                      style={{
                        width: '21px',
                        margin: '0 5px 0 -26px',
                        verticalAlign: 'middle'
                      }}
                    />
                    <div
                      style={{
                        display: 'inline-flex',
                        gap: '0.5rem',
                        justifyContent: 'space-between',
                        minWidth: '100%'
                      }}
                    >
                      <div style={{ minWidth: 'calc(100% - 12rem)' }}>
                        <Typography.Text strong={true}>
                          {project.projectName}
                        </Typography.Text>{' '}
                        <Typography.Text italic={true}>
                          {project.projectId}
                        </Typography.Text>
                        <FeatureIcons project={project} />
                      </div>
                      <div style={{ minWidth: '5rem', textAlign: 'center' }}>
                        {project.subscriptionTier && (
                          <SubscriptionTier tier={project.subscriptionTier} />
                        )}
                      </div>
                      <div style={{ minWidth: '6rem' }}>
                        {project.isClosedOut && <ClosedOut />}
                      </div>
                    </div>
                  </div>
                ))}
              </details>
            )}
          </Col>
        )
      }
    },
    {
      title: 'Creation Date',
      dataIndex: 'createdAt',
      width: '10rem',
      render: createdAt => {
        return (
          <Typography.Text>
            {moment(createdAt).local().format('DD/MM/YYYY HH:mm')}
          </Typography.Text>
        )
      }
    }
  ]

  columns.push({
    title: 'Integrations',
    key: 'edit',
    width: '7.5rem',

    render: accounts => (
      <>
        <Typography.Text
          style={{
            lineHeight: '1.25',
            margin: '0.5rem 0',
            display: 'block'
          }}
        >
          <Link
            to={generatePath(ROUTES.ONECLICK_LCA.EDIT, {
              accountId: accounts.accountId
            })}
          >
            One Click LCA
          </Link>
        </Typography.Text>
        <Typography.Text
          style={{
            lineHeight: '1.25',
            margin: '0.5rem 0',
            display: 'block'
          }}
        >
          <Link
            to={generatePath(ROUTES.PROCORE.EDIT, {
              accountId: accounts.accountId
            })}
          >
            Procore
          </Link>
        </Typography.Text>
      </>
    )
  })

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        margin: '0.5rem 0.75rem 0'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          flexWrap: 'wrap',
          margin: '1rem 1rem 0 1rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Switch checked={includeClosedOut} onChange={setIncludeClosedOut} />
          <Typography.Text style={{ fontWeight: 600 }}>
            Include Closed Out
          </Typography.Text>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            fontSize: '0.875rem'
          }}
        >
          <Typography.Text style={{ fontWeight: 500 }}>Key:</Typography.Text>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.AppstoreOutlined style={{ color: colours.GREEN }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Extended Product Groups
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.UnorderedListOutlined style={{ color: colours.BLUE }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Uniclass Codes
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.BulbOutlined style={{ color: colours.AMBER }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Design Matches
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.ApiOutlined style={{ color: '#9C27B0' }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Autodesk Enabled
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.ExperimentOutlined style={{ color: colours.TEAL }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              One Click LCA Enabled
            </Typography.Text>
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={formatTableData()}
        pagination={false}
        scroll={{ y: `calc(100vh - 240px)` }}
        sticky
        style={{ margin: '1rem 0 0' }}
        rowClassName={row => row.accountId}
      />
    </div>
  )
}

AccountsList.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.object)
}

function ClosedOut() {
  return (
    <Tooltip title="The Project is Closed Out and new tickets will not arrive. NOTE: Currently there is no prevention of new Connections being created.">
      <ClosedOutBadge strong={true}>Closed Out</ClosedOutBadge>
    </Tooltip>
  )
}

function FeatureIcons({ project }) {
  return (
    <span style={{ marginLeft: '0.5rem' }}>
      {project.isExtendedProductGroupsEnabled && (
        <Tooltip title="Extended Product Groups Enabled">
          <Icons.AppstoreOutlined
            style={{ color: colours.GREEN, marginLeft: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isUniclassCodesEnabled && (
        <Tooltip title="Uniclass Codes Enabled">
          <Icons.UnorderedListOutlined
            style={{ color: colours.BLUE, marginLeft: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isDesignMatchesEnabled && (
        <Tooltip title="Design Matches Enabled">
          <Icons.BulbOutlined
            style={{ color: colours.AMBER, marginLeft: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isAutodeskEnabled && (
        <Tooltip title="Autodesk Enabled">
          <Icons.ApiOutlined
            style={{ color: '#9C27B0', marginLeft: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isOneClickLcaEnabled && (
        <Tooltip title="One Click LCA Enabled">
          <Icons.ExperimentOutlined
            style={{ color: colours.TEAL, marginLeft: '0.25rem' }}
          />
        </Tooltip>
      )}
    </span>
  )
}

function SubscriptionTier({ tier }) {
  let tagColour
  switch (tier) {
    case SUBSCRIPTION_TIER.LEGACY:
      tagColour = `${colours.ORANGE}CC`
      break
    case SUBSCRIPTION_TIER.CAPTURE:
      tagColour = `${colours.GREEN}99`
      break
    case SUBSCRIPTION_TIER.COMPLY:
      tagColour = `${colours.GREEN}BB`
      break
    case SUBSCRIPTION_TIER.CONSULT:
      tagColour = `${colours.GREEN}DD`
      break
    case SUBSCRIPTION_TIER.PIONEERS:
      tagColour = `${colours.AMBER}DD`
      break
    default:
      tagColour = `${colours.GREEN}DD`
  }
  return (
    <SubscriptionTierBadge strong={true} style={{ backgroundColor: tagColour }}>
      {tier}
    </SubscriptionTierBadge>
  )
}

export const SubscriptionTierBadge = styled(Typography.Text)`
  display: inline-block;
  background-color: ${colours.GREEN};
  color: ${colours.WHITE};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
export const ClosedOutBadge = styled(Typography.Text)`
  display: inline-block;
  min-width: 32px;
  background-color: ${colours.WHITE};
  color: ${colours.RED} !important;
  border: 1px solid ${colours.RED};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
